import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./EmailInputWithDomainSuggest.css";

export const POPULAR_EMAIL_DOMAINS = [
  "gmail.com",
  "outlook.com",
  "yahoo.com",
  "hotmail.com",
  "icloud.com",
  "protonmail.com",
  "live.com",
  "msn.com",
  "aol.com",
  "zoho.com",
  "gmx.com",
  "me.com",
  "ymail.com",
  "comcast.net",
  "verizon.net",
  "yandex.com",
  "mail.com",
  "inbox.com",
  "gmx.net",
  "tutanota.com",
];

export const getEmailDomainSuggestions = (value) => {
  const s = String(value || "");
  const at = s.indexOf("@");
  if (at < 0) return [];
  if (s.indexOf("@", at + 1) >= 0) return [];
  const afterAt = s.slice(at + 1);
  if (/\s/.test(afterAt)) return [];
  const q = afterAt.toLowerCase();
  if (POPULAR_EMAIL_DOMAINS.includes(q)) return [];
  if (q === "") {
    return [...POPULAR_EMAIL_DOMAINS];
  }
  return POPULAR_EMAIL_DOMAINS.filter((d) => d.startsWith(q));
};

const blurPayload = (name, id, value) => ({
  type: "blur",
  target: { name: name || "", id: id || "", value: value ?? "" },
  currentTarget: { name: name || "", value: value ?? "" },
});

/**
 * Email text field with @ popular-domain hint list. Use anywhere users enter an email.
 */
const EmailInputWithDomainSuggest = ({
  value = "",
  onChange,
  onBlur,
  name,
  id,
  placeholder,
  disabled = false,
  readOnly = false,
  autoComplete = "email",
  autoCapitalize = "off",
  autoCorrect = "off",
  spellCheck = false,
  inputClassName,
  wrapClassName = "email_code eids-email-wrap",
  inputMode = "email",
  style: inputStyle,
  trimOnBlur = true,
  suggestionsEnabled = true,
}) => {
  const resolvedInputClassName = inputClassName !== undefined && inputClassName !== null ? inputClassName : "input_filed";
  const [listFocused, setListFocused] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const blurTimerRef = useRef(null);

  const clearBlurTimer = useCallback(() => {
    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearBlurTimer(), [clearBlurTimer]);

  const suggestions = useMemo(
    () => (suggestionsEnabled ? getEmailDomainSuggestions(value) : []),
    [suggestionsEnabled, value]
  );

  const listKey = useMemo(() => suggestions.join(","), [suggestions]);
  useEffect(() => {
    setHighlight(0);
  }, [listKey]);

  const applyDomain = useCallback(
    (domain) => {
      const s = String(value || "");
      const at = s.indexOf("@");
      const newVal = at < 0 ? s : `${s.slice(0, at)}@${domain}`;
      onChange?.({
        type: "change",
        target: { name: name || "", id: id || "", value: newVal },
        currentTarget: { name, value: newVal },
      });
    },
    [name, id, onChange, value]
  );

  const onWrapFocus = useCallback(() => {
    clearBlurTimer();
    setListFocused(true);
  }, [clearBlurTimer]);

  const onWrapBlur = useCallback(() => {
    clearBlurTimer();
    const raw = String(value ?? "");
    const trimmed = raw.trim();
    if (trimOnBlur && trimmed !== raw) {
      onChange?.({
        type: "change",
        target: { name: name || "", id: id || "", value: trimmed },
        currentTarget: { name, value: trimmed },
      });
    }
    const after = trimOnBlur ? trimmed : raw;
    blurTimerRef.current = window.setTimeout(() => {
      setListFocused(false);
      onBlur?.(blurPayload(name, id, after));
    }, 180);
  }, [clearBlurTimer, trimOnBlur, value, onChange, name, id, onBlur]);

  const onKeyDown = useCallback(
    (e) => {
      if (!suggestionsEnabled || !listFocused || !String(value || "").includes("@") || suggestions.length === 0) {
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h + 1) % suggestions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const pick = suggestions[highlight];
        if (pick) applyDomain(pick);
      } else if (e.key === "Escape") {
        setListFocused(false);
      }
    },
    [suggestionsEnabled, listFocused, value, suggestions, highlight, applyDomain]
  );

  const showList = listFocused && suggestions.length > 0;

  return (
    <div className={wrapClassName}>
      <input
        id={id}
        name={name}
        className={resolvedInputClassName}
        type="text"
        inputMode={inputMode}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={onWrapFocus}
        onBlur={onWrapBlur}
        onKeyDown={onKeyDown}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        autoCorrect={autoCorrect}
        spellCheck={spellCheck}
        disabled={disabled}
        readOnly={readOnly}
        style={inputStyle}
      />
      {showList ? (
        <ul className="eids-email-suggest" role="listbox" aria-label="Email domains">
          {suggestions.map((domain, i) => (
            <li
              key={domain}
              role="option"
              aria-selected={i === highlight}
              className={i === highlight ? "is-active" : undefined}
              onMouseDown={(e) => {
                e.preventDefault();
                clearBlurTimer();
                applyDomain(domain);
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              @{domain}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default EmailInputWithDomainSuggest;
